import { useTranslation } from "react-i18next"

import { PriceRange } from "@/components/brand/settings/products/price-range"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { missionProductParamNames } from "@/lib/mission-product-param-names"
import type { AttachedCatalogProduct } from "@/types/missions"

/**
 * OKS-933 — S2's product summary card (UX Spec §2).
 *
 * Renders ONLY when a catalog product is attached. `product_id IS NULL` is S3,
 * and S3 has no card — thumbnail, title and price range all read from
 * `products`, so there is nothing to summarise. That is why the
 * product-deleted orphan is an S3 sub-state (S3b) rather than "S2 with a null
 * product": rendering this with no source would invent a card.
 *
 * ⚠️ Everything here is DISPLAY. What the mission gifts is the snapshot on
 * `mission_products` (`name` / `value` / `currency` / `sku`), rendered by the
 * inputs below this card and editable per mission (AC5). Do not "fix" a
 * divergence between the two by rendering `product.title` in place of the
 * snapshot — the divergence IS the feature.
 */
export interface MissionProductSummaryCardProps {
  product: AttachedCatalogProduct
  /** False once the mission has collabs — Detach disappears entirely (D2). */
  canDetach: boolean
  /** OKS-1040 — the offered row this card belongs to (0 = legacy names). */
  index?: number
}

export function MissionProductSummaryCard({
  product,
  canDetach,
  index = 0,
}: MissionProductSummaryCardProps) {
  const { t } = useTranslation()

  return (
    <div
      className="bg-muted/40 flex flex-col gap-3 rounded-md p-3 sm:flex-row sm:items-start"
      data-testid="mission-product-summary"
    >
      {product.image_url ? (
        <img
          src={product.image_url}
          alt=""
          className="size-16 shrink-0 rounded object-cover"
        />
      ) : (
        <span
          className="bg-muted size-16 shrink-0 rounded"
          aria-hidden="true"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm font-medium">{product.title}</span>
        {/* OKS-1008 — addressable, because the price is no longer a LEAF node.
            D7-3 moved the en dash into its own `<span aria-hidden>`, so a
            locator that looked for "the only leaf <span> carrying a digit"
            (which is what the arm did, for want of a handle) now finds
            nothing. A heuristic locator over an unnamed node fails the moment
            the node's shape changes; a testid is the contract. */}
        <span
          className="text-muted-foreground text-xs"
          data-testid="mission-product-summary-price"
        >
          <PriceRange
            minCents={product.min_price_cents}
            maxCents={product.max_price_cents}
            currency={product.currency}
          />
        </span>

        {product.archived && (
          /* OKS-946 — the `whitespace-normal` override that used to live here is
             GONE, and so is the hazard it documented: `ui/badge.tsx` no
             longer declares `whitespace-nowrap`, so this badge inherits
             `normal` from its (non-table) ancestors and wraps by default. */
          <Badge
            variant="secondary"
            className="mt-1"
            data-testid="mission-product-archived-chip"
          >
            {t("missions.form.product.no_longer_in_catalog")}
          </Badge>
        )}
      </div>

      {/* D2 — there is no "Change product" affordance anywhere. `product_id` is
          write-once; the only route back is Detach, and Detach is what carries
          the collabs gate. */}
      <div className="flex shrink-0 flex-col gap-2 sm:w-auto">
        {canDetach ? (
          <Button
            type="submit"
            name={missionProductParamNames(index).detachProduct}
            value="1"
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            data-testid="mission-product-detach"
          >
            {t("missions.form.product.detach")}
          </Button>
        ) : (
          /* Absent, not disabled-with-a-tooltip (UX §2). The server refusal is
             the guard regardless. */
          <p
            className="text-muted-foreground text-xs"
            data-testid="mission-product-detach-locked"
          >
            {t("missions.form.product.detach_locked")}
          </p>
        )}
      </div>
    </div>
  )
}

export default MissionProductSummaryCard
